import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  FireIcon,
  BoltIcon,
  HeartIcon,
  MusicalNoteIcon,
  ArrowPathIcon,
  TrophyIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { FONTS, HeroCarousel, ReadyToStart } from './ServicePageTemplate';

// ─── IMPORT LOCAL IMAGES ──────────────────────────────────────────────────────
import flowstepsImg from '../../assets/images/flowsteps.jpg';
import legendbootcampImg from '../../assets/images/legendbootcamp.jpg';
import urbanhiitImg from '../../assets/images/urbanhiit.jpg';
import strengthImg from '../../assets/images/strength.jpg';
import combogriitImg from '../../assets/images/combogriit.jpg';
import afrobeatImg from '../../assets/images/afrobeat.jpg';
import hydroxImg from '../../assets/images/hydrox.jpg';
import cyclexImg from '../../assets/images/cyclex.jpg';
import studioImage from '../../assets/images/studio.jpg';

// ─── WHATSAPP NUMBER (ENQUIRY NUMBER: 0117110000) ────────────────────────────
const WA_NUMBER = '0117110000';

// ─── HERO PHOTOS ARRAY ────────────────────────────────────────────────────────
const HERO_PHOTOS = [studioImage, studioImage, studioImage, studioImage];

// ─── GALLERY PHOTOS - Unorganized layout with different sizes ─────────────────
const GALLERY_PHOTOS = [
  { src: studioImage, size: 'large' },
  { src: studioImage, size: 'small' },
  { src: studioImage, size: 'medium' },
  { src: studioImage, size: 'small' },
  { src: studioImage, size: 'large' },
  { src: studioImage, size: 'medium' },
  { src: studioImage, size: 'small' },
  { src: studioImage, size: 'medium' },
  { src: studioImage, size: 'large' },
];

// ─── ALL CLASSES DATA with benefits AND MATCHING LOCAL IMAGES ─────────────────
const ALL_CLASSES = [
  {
    id: 1,
    title: 'STRENGTH & CONDITIONING',
    description: 'A full-body performance class focused on building muscular strength, endurance, power, and athletic conditioning using weights, functional movements, and high-intensity training methods.',
    benefits: ['BUILD STRENGTH', 'IMPROVE POWER', 'TOTAL-BODY FITNESS'],
    image: strengthImg,
    icon: FireIcon,
    accentColor: '#e63946',
  },
  {
    id: 2,
    title: 'COMBO-GRIT',
    description: 'A hybrid workout blending cardio, strength circuits, bodyweight training, and endurance drills designed to push mental toughness and total-body fitness.',
    benefits: ['BURN CALORIES', 'BUILD ENDURANCE', 'MENTAL TOUGHNESS'],
    image: combogriitImg,
    icon: BoltIcon,
    accentColor: '#f4a261',
  },
  {
    id: 3,
    title: 'URBAN HIIT',
    description: 'A fast-paced high-intensity interval training class combining explosive cardio bursts, functional exercises, and urban-style energy to maximize fat burn and stamina.',
    benefits: ['MAX FAT BURN', 'BOOST STAMINA', 'IMPROVE CARDIO'],
    image: urbanhiitImg,
    icon: BoltIcon,
    accentColor: '#2a9d8f',
  },
  {
    id: 4,
    title: 'AFROBEAT',
    description: 'A high-energy dance fitness class combining Afrobeat music and movement for a fun cardio workout that improves coordination, endurance, and calorie burn.',
    benefits: ['FUN & ENERGIZING', 'BURN CALORIES', 'IMPROVE COORDINATION'],
    image: afrobeatImg,
    icon: MusicalNoteIcon,
    accentColor: '#e9c46a',
  },
  {
    id: 5,
    title: 'CYCLE X',
    description: 'An intense indoor cycling experience focused on endurance, leg strength, speed intervals, and cardiovascular conditioning through rhythm-driven rides.',
    benefits: ['BUILD ENDURANCE', 'LEG STRENGTH', 'CARDIO POWER'],
    image: cyclexImg,
    icon: ArrowPathIcon,
    accentColor: '#06c',
  },
  {
    id: 6,
    title: 'HYROX',
    description: 'A performance-based fitness class inspired by hybrid athletic racing, combining functional strength exercises with endurance stations such as sled pushes, rowing, running, and conditioning work.',
    benefits: ['RACE-INSPIRED TRAINING', 'STRENGTH & ENDURANCE', 'TOTAL ATHLETIC PERFORMANCE'],
    image: hydroxImg,
    icon: TrophyIcon,
    accentColor: '#800020',
  },
  {
    id: 7,
    title: 'FLOW STEPS',
    description: 'A rhythm-based cardio and step workout combining coordinated footwork, movement patterns, and music to improve agility, balance, fitness, and coordination.',
    benefits: ['IMPROVE AGILITY', 'BETTER BALANCE', 'RHYTHM & FITNESS'],
    image: flowstepsImg,
    icon: HeartIcon,
    accentColor: '#6a0572',
  },
  {
    id: 8,
    title: 'LEGENDS BOOTCAMP',
    description: 'An elite-style team training session designed to challenge strength, endurance, teamwork, discipline, and mental toughness through varied functional workouts and outdoor-style conditioning.',
    benefits: ['TEAMWORK', 'BUILD TOUGHNESS', 'ELITE CONDITIONING'],
    image: legendbootcampImg,
    icon: StarIcon,
    accentColor: '#0077b6',
  },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
  viewport: { once: true },
});

const buildWhatsAppMessage = (classData) => {
  const msg = `Hi PowerCore! 👋\n\nI'm interested in the *${classData.title}* class.\n\n📝 Description: ${classData.description}\n\n✨ Benefits:\n${classData.benefits.map(b => `• ${b}`).join('\n')}\n\nPlease let me know how I can join. Thank you!`;
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
};

// ─── MODERN HORIZONTAL STRIP CARD (Image on left, content on right) ──────────
const ClassCard = ({ classData, index }) => {
  const Icon = classData.icon;
  const whatsappUrl = buildWhatsAppMessage(classData);

  return (
    <motion.div
      initial={{ opacity: 0, x: -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      viewport={{ once: true }}
      className="group rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1 flex flex-row"
    >
      {/* Image on Left */}
      <div className="relative flex-shrink-0 w-48 md:w-56 overflow-hidden">
        <img 
          src={classData.image} 
          alt={classData.title}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
          style={{ height: '100%', minHeight: '200px' }}
        />
        <div 
          className="absolute top-3 left-3 w-8 h-8 rounded-xl flex items-center justify-center shadow-lg"
          style={{ background: classData.accentColor }}
        >
          <Icon className="h-4 w-4 text-white" />
        </div>
      </div>
      
      {/* Content on Right */}
      <div className="flex-1 p-5 flex flex-col">
        <h3 className="font-black text-gray-900 text-lg md:text-xl mb-2 leading-tight"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {classData.title}
        </h3>
        
        <p className="text-gray-600 text-sm leading-relaxed mb-3">
          {classData.description}
        </p>
        
        {/* Benefits Tags - Each class has its own 3 benefits */}
        <div className="flex flex-wrap gap-2 mb-4">
          {classData.benefits.map((benefit, idx) => (
            <span 
              key={idx}
              className="text-[10px] font-bold px-2 py-1 rounded-full"
              style={{ background: classData.accentColor + '15', color: classData.accentColor }}
            >
              {benefit}
            </span>
          ))}
        </div>
        
        {/* WhatsApp Enquire Button */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white transition-all hover:scale-[1.02] self-start"
          style={{ background: '#25D366' }}
        >
          <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
          Enquire on WhatsApp
        </a>
      </div>
    </motion.div>
  );
};

// ─── UNORGANIZED PHOTO GALLERY (Pinterest-style masonry layout) ──────────────
const UnorganizedGallery = ({ photos }) => (
  <section className="py-16 bg-white">
    <div className="container mx-auto px-4 max-w-7xl">
      <motion.div {...fade(0)} className="mb-10 text-center">
        <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block text-primary">
          Our Space
        </span>
        <h2 className="font-black text-gray-900"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem,3.5vw,2.8rem)' }}>
          EXPLORE PCF STUDIO
        </h2>
      </motion.div>
      
      {/* Masonry-style unorganized grid */}
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
        {photos.map((photo, i) => {
          // Random heights for unorganized look
          const heights = ['h-64', 'h-80', 'h-56', 'h-96', 'h-72', 'h-48', 'h-88', 'h-64', 'h-80'];
          const randomHeight = heights[i % heights.length];
          
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: (i % 5) * 0.05 }}
              viewport={{ once: true }}
              className="break-inside-avoid mb-4 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <img 
                src={photo.src} 
                alt={`Gallery ${i + 1}`}
                className={`w-full ${randomHeight} object-cover object-center hover:scale-105 transition-transform duration-500`}
              />
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT - YogaService
// ═══════════════════════════════════════════════════════════════════════════════
const YogaService = () => {
  const DATA = {
    tagline: 'TRAIN HARD · RECOVER SMART · LIVE POWERFUL',
    heroText: 'Expert-led classes that build real strength, endurance and athletic performance — for all levels.',
    waMessage: encodeURIComponent("Hi PowerCore! 💪 I'm interested in your classes. Could you tell me more about the schedule and how to join?"),
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{FONTS}</style>
      
      {/* 1. Hero Slider */}
      <HeroCarousel photos={HERO_PHOTOS} {...DATA} />
      
      {/* 2. Classes Section - Horizontal Strip Cards with Benefits */}
      <section className="py-20" style={{ background: '#faf8f8' }}>
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div {...fade(0)} className="mb-12 text-center">
            <span className="text-xs font-bold tracking-[0.2em] uppercase mb-3 inline-block text-primary">
              Our Classes
            </span>
            <h2 className="font-black text-gray-900 mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2rem,4vw,3.5rem)' }}>
              TRAIN WITH THE BEST
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto">
              Discover our diverse range of classes designed to help you reach your fitness goals
            </p>
          </motion.div>

          {/* Horizontal Cards Grid - Each card has its own 3 benefits */}
          <div className="space-y-4">
            {ALL_CLASSES.map((classData, i) => (
              <ClassCard
                key={classData.id}
                classData={classData}
                index={i}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 3. Unorganized Gallery - Pinterest-style masonry layout */}
      <UnorganizedGallery photos={GALLERY_PHOTOS} />

      {/* 4. Ready to Start CTA */}
      <ReadyToStart />
    </div>
  );
};

export default YogaService;